import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { quizAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiClock, FiCheckCircle, FiAlertCircle, FiAward, FiFlag, FiSettings, FiGlobe, FiChevronRight, FiChevronLeft, FiPause, FiPlay } from 'react-icons/fi';
import DOMPurify from 'dompurify';

// Bilingual Content - Testbook Style
const translations = {
  en: {
    timeLeft: 'Time Left',
    question: 'Question',
    of: 'of',
    answered: 'Answered',
    notAnswered: 'Not Answered',
    notVisited: 'Not Visited',
    markedForReview: 'Marked for Review',
    answeredMarkedForReview: 'Answered & Marked for Review',
    voice: 'Voice',
    submitTest: 'Submit Test',
    marks: 'Marks',
    negative: 'Negative',
    viewInstructions: 'View Instructions',
    questionPalette: 'Question Palette',
    markForReview: 'Mark for Review & Next',
    clearResponse: 'Clear Response',
    saveAndNext: 'Save & Next',
    previous: 'Previous',
    submit: 'Submit',
    submitExam: 'Submit Test',
    reviewAnswers: 'Review your answers before final submission',
    warning: 'Warning!',
    warningMessage: 'You have not answered {count} question(s). They will be considered wrong.',
    allAnswered: 'Great! All questions answered',
    goBack: 'Go Back',
    submitNow: 'Yes, Submit Test',
    language: 'Language',
    selectLanguage: 'Select Language',
    pauseTimer: 'Pause Timer',
    resumeTimer: 'Resume Timer',
    timerPaused: 'Timer Paused',
    timerResumed: 'Timer Resumed'
  },
  hi: {
    timeLeft: 'शेष समय',
    question: 'प्रश्न',
    of: 'में से',
    answered: 'उत्तरित',
    notAnswered: 'अनुत्तरित',
    notVisited: 'अदेखे',
    markedForReview: 'समीक्षा के लिए चिह्नित',
    answeredMarkedForReview: 'उत्तरित और चिह्नित',
    voice: 'आवाज़',
    submitTest: 'परीक्षा जमा करें',
    marks: 'अंक',
    negative: 'नकारात्मक',
    viewInstructions: 'निर्देश देखें',
    questionPalette: 'प्रश्न पैलेट',
    markForReview: 'चिह्नित करें और अगला',
    clearResponse: 'उत्तर मिटाएं',
    saveAndNext: 'सहेजें और अगला',
    previous: 'पिछला',
    submit: 'जमा करें',
    submitExam: 'परीक्षा जमा करें',
    reviewAnswers: 'अंतिम सबमिशन से पहले अपने उत्तरों की समीक्षा करें',
    warning: 'चेतावनी!',
    warningMessage: 'आपने {count} प्रश्नों का उत्तर नहीं दिया है। वे गलत माने जाएंगे।',
    allAnswered: 'बढ़िया! सभी प्रश्नों के उत्तर दिए गए',
    goBack: 'वापस जाएं',
    submitNow: 'हाँ, परीक्षा जमा करें',
    language: 'भाषा',
    selectLanguage: 'भाषा चुनें',
    pauseTimer: 'टाइमर रोकें',
    resumeTimer: 'टाइमर शुरू करें',
    timerPaused: 'टाइमर रोका गया',
    timerResumed: 'टाइमर शुरू किया गया'
  }
};

const QuizPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { questions = [], config } = location.state || {};

  // Parse duration from HH:MM:SS format to total seconds
  const parseDuration = (duration) => {
    if (!duration) return 600; // default 10 minutes
    const [hours, mins, secs] = duration.split(':').map(Number);
    return (hours * 3600) + (mins * 60) + secs;
  };

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [markedForReview, setMarkedForReview] = useState({});
  const [visited, setVisited] = useState({ 0: true }); // Track visited questions
  const [showConfirm, setShowConfirm] = useState(false);
  const [timeLeft, setTimeLeft] = useState(() => 
    parseDuration(config?.examDetails?.duration) || config?.numQuestions * 60 || 600
  );
  const [isPaused, setIsPaused] = useState(false);
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);
  
  // Language State
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('quizLanguage') || 'en';
  });

  const t = translations[language];
  
  const marks = config?.marks || 1;
  const negativeMarks = config?.negativeMarks || 0;

  // Voice Settings State
 

  const [sttSettings, setSTTSettings] = useState({
    enabled: false,
    language: language === 'hi' ? 'hi-IN' : 'en-IN',
    autoSend: false
  });



  // Toggle Language
  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'hi' : 'en';
    setLanguage(newLang);
    localStorage.setItem('quizLanguage', newLang);
  
    toast.success(
      newLang === 'hi' ? 'भाषा हिंदी में बदल गई' : 'Language changed to English',
      { autoClose: 2000 }
    );
  };

  // Load voice settings from localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('voiceSettings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
     
        if (parsed.stt) setSTTSettings(parsed.stt);
      } catch (err) {
        console.error('Error loading voice settings:', err);
      }
    }
  }, []);

  useEffect(() => {
    if (!questions.length) {
      navigate('/quiz-selection');
      return;
    }

    // Timer - only runs when not paused
    const timer = setInterval(() => {
      if (!isPaused) {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isPaused]);

  const currentQ = questions[currentQuestion];
  const options = config?.useAI 
    ? currentQ?.options || []
    : [
        { text: currentQ?.opt_a, label: 'A' },
        { text: currentQ?.opt_b, label: 'B' },
        { text: currentQ?.opt_c, label: 'C' },
        { text: currentQ?.opt_d, label: 'D' },
      ].filter(opt => opt.text);

  const handleAnswerSelect = (answer) => {
    setAnswers({
      ...answers,
      [currentQuestion]: {
        question_id: currentQ.id,
        selected: answer,
      },
    });
  };

  // Auto-read question when it changes (if enabled)
 

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      if (isPaused) setIsPaused(false);
      const nextQ = currentQuestion + 1;
      setCurrentQuestion(nextQ);
      setVisited(prev => ({ ...prev, [nextQ]: true }));
    }
  };

  const togglePause = () => {
    setIsPaused((prev) => !prev);
    toast.info(
      isPaused ? (language === 'hi' ? t.timerResumed : 'Timer Resumed') : (language === 'hi' ? t.timerPaused : 'Timer Paused'),
      { autoClose: 1500 }
    );
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
    
      const prevQ = currentQuestion - 1;
      setCurrentQuestion(prevQ);
      setVisited(prev => ({ ...prev, [prevQ]: true }));
    }
  };

  const goToQuestion = (index) => {
    setCurrentQuestion(index);
    setVisited(prev => ({ ...prev, [index]: true }));
  };

  const handleSaveAndNext = () => {
    // if (!answers[currentQuestion]) {
    //   toast.warning(language === 'hi' ? 'कृपया एक उत्तर चुनें' : 'Please select an answer');
    //   return;
    // }
    if (isPaused) setIsPaused(false);
    handleNext();
  };

  const handleClearResponse = () => {
    const newAnswers = { ...answers };
    delete newAnswers[currentQuestion];
    setAnswers(newAnswers);
    toast.info(language === 'hi' ? 'उत्तर मिटा दिया गया' : 'Response cleared');
  };

  const handleMarkForReviewAndNext = () => {
    if (isPaused) setIsPaused(false);
    setMarkedForReview({
      ...markedForReview,
      [currentQuestion]: true
    });
    handleNext();
  };

  // Handle speak question
 

  const handleMarkForReview = () => {
    setMarkedForReview({
      ...markedForReview,
      [currentQuestion]: !markedForReview[currentQuestion]
    });
  };

  const handleSubmit = async () => {
    try {
      const answerArray = Object.values(answers);
      
      // Prepare submission data
      const submissionData = {
        userId: user.id,
        answers: answerArray,
        config: config
      };
      
      const response = await quizAPI.submitQuiz(submissionData);
      
      navigate('/results', { 
        state: { 
          results: response.data,
          questions,
          answers,
          config
        } 
      });
    } catch (error) {
      console.error('Error submitting quiz:', error);
      alert('Failed to submit quiz. Please try again.');
    }
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);
  const seconds = timeLeft % 60;
  const answeredCount = Object.keys(answers).length;
  const reviewedCount = Object.keys(markedForReview).filter(key => markedForReview[key]).length;
  const notVisitedCount = questions.length - Object.keys(visited).length;
  const notAnsweredCount = Object.keys(visited).length - answeredCount;
  const answeredAndMarkedCount = Object.keys(answers).filter(key => markedForReview[key]).length;

  // Get question status for palette
  const getQuestionStatus = (index) => {
    if (!visited[index]) return 'not-visited';
    if (answers[index] && markedForReview[index]) return 'answered-marked';
    if (markedForReview[index]) return 'marked';
    if (answers[index]) return 'answered';
    return 'not-answered';
  };
  console.log("questions",questions)

  return (
    <>
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
      
      <div className="min-h-screen bg-gray-100 overflow-hidden"  style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
        {/* Testbook Style Header - Mobile Optimized */}
      <div className="sticky top-0 z-50 bg-white shadow-md border-b border-gray-200">
        <div className="px-2 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between">
            {/* Left - Question Counter */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 sm:gap-2">
                <span className="hidden sm:inline text-[10px] sm:text-xs font-semibold text-gray-600">{t.question}</span>
                <span className="px-2 sm:px-2.5 py-1 sm:py-0.5 bg-blue-600 text-white rounded text-xs sm:text-xs font-bold">
                  {currentQuestion + 1}/{questions.length}
                </span>
              </div>
            </div>

            {/* Center - Timer with Pause/Play */}
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 rounded-md font-bold ${
                timeLeft < 60 
                  ? 'bg-red-100 text-red-700' 
                  : timeLeft < 300
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-green-100 text-green-700'
              }`}>
                <FiClock className="w-4 sm:w-4 h-4 sm:h-4" />
                <span className="text-sm sm:text-base tabular-nums">
                  {hours > 0 && `${hours}:`}
                  {minutes < 10 ? `0${minutes}` : minutes}:{seconds < 10 ? `0${seconds}` : seconds}
                </span>
              </div>
              <button
                type="button"
                onClick={togglePause}
                className={`p-1.5 rounded-md transition-colors ${
                  isPaused 
                    ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
                title={isPaused ? t.resumeTimer : t.pauseTimer}
              >
                {isPaused ? (
                  <FiPlay className="w-4 h-4" />
                ) : (
                  <FiPause className="w-4 h-4" />
                )}
              </button>
            </div>

            {/* Right - Language & Submit */}
            <div className="flex items-center gap-1 sm:gap-2">
              {/* <button
                onClick={toggleLanguage}
                className="p-1.5 sm:p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                title={t.selectLanguage}
              >
                <FiGlobe className="w-4 sm:w-4 h-4 sm:h-4 text-gray-600" />
              </button> */}

              <button
                onClick={() => setShowConfirm(true)}
                className="px-2 sm:px-4 py-1.5 sm:py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors text-xs sm:text-sm"
              >
                {t.submitTest}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Horizontal Question Palette - Mobile Only */}
      <div className="lg:hidden sticky top-[60px] z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 px-3 py-2 min-w-max">
            {questions.map((_, index) => {
              const status = getQuestionStatus(index);
              const isCurrent = index === currentQuestion;
              
              let bgClass = 'bg-gray-300 text-gray-700';
              if (status === 'answered') bgClass = 'bg-green-600 text-white';
              else if (status === 'not-answered') bgClass = 'bg-red-600 text-white';
              else if (status === 'marked') bgClass = 'bg-purple-600 text-white';
              else if (status === 'answered-marked') bgClass = 'bg-purple-600 text-white';
              
              return (
                <button
                  key={index}
                  onClick={() => goToQuestion(index)}
                  className={`
                    flex-shrink-0 w-10 h-10 rounded-md font-bold text-sm
                    transition-all duration-200
                    ${bgClass}
                    ${isCurrent ? 'ring-4 ring-blue-500 ring-offset-2 scale-110' : 'active:scale-95'}
                  `}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content - Mobile First */}
      <div className="flex flex-col lg:flex-row h-[calc(100vh-60px)] lg:h-[calc(100vh-64px)]">
        
        {/* Question Area */}
        <div className="flex-1 overflow-y-auto bg-white pb-56 lg:pb-0">
          <div className="px-3 sm:px-4 py-3 sm:py-4 max-w-4xl mx-auto">
            {/* Question Section - Mobile Optimized */}
            <div className="mb-6">
              {/* Question Header */}
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <span className="text-xs font-semibold text-gray-500">
                      {t.question} {currentQuestion + 1}
                    </span>
                    {markedForReview[currentQuestion] && (
                      <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded font-medium flex items-center gap-1">
                        <FiFlag className="w-3 h-3" />
                        <span className="hidden sm:inline">{t.markedForReview}</span>
                      </span>
                    )}
                  </div>
                  <h2
                    className="text-base sm:text-lg font-semibold text-gray-900 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentQ?.question || '') }}
                  />
                </div>
                
               
              </div>

              {/* Options - Mobile Touch Optimized */}
              <div className="space-y-3">
                {options.map((option, index) => {
                  const optionLabel = config?.useAI 
                    ? String.fromCharCode(65 + index)
                    : option.label;
                  const optionText = config?.useAI ? option.text : option.text;
                  const isSelected = answers[currentQuestion]?.selected === optionLabel;

                  return (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(optionLabel)}
                      className={`w-full text-left p-4 sm:p-3 rounded-lg sm:rounded-md border-2 transition-all active:scale-[0.98] ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 active:border-gray-300 bg-white active:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-3 sm:gap-2.5">
                        <div className={`flex-shrink-0 w-6 h-6 sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                          isSelected
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300'
                        }`}>
                          {isSelected && (
                            <div className="w-2.5 sm:w-2 h-2.5 sm:h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <span className="font-semibold text-gray-700 mr-1.5 text-sm sm:text-sm">
                            {optionLabel}.
                          </span>
                          <span
                            className={`text-gray-800 text-sm sm:text-sm leading-relaxed ${isSelected ? 'font-medium' : ''}`}
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(optionText || '') }}
                          />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Desktop Action Buttons */}
            <div className="hidden lg:flex flex-wrap gap-2 mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={handleMarkForReviewAndNext}
                className="px-4 py-2 border-2 border-purple-600 text-purple-600 rounded-md text-sm font-medium hover:bg-purple-50 transition-colors"
              >
                {t.markForReview}
              </button>
              <button
                onClick={handleClearResponse}
                disabled={!answers[currentQuestion]}
                className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t.clearResponse}
              </button>
              <div className="flex-1"></div>
              <button
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
                className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <FiChevronLeft className="w-4 h-4" />
                {t.previous}
              </button>
              <button
                onClick={handleSaveAndNext}
                className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors flex items-center gap-1"
              >
                {t.saveAndNext}
                <FiChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Fixed Bottom Action Bar - Mobile Only */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 p-3 lg:hidden z-50 shadow-lg">
          <div className="flex flex-col gap-2">
          <div className="flex gap-2">
              <button
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
                className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg text-sm font-bold active:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
              >
                <FiChevronLeft className="w-5 h-5" />
                {t.previous}
              </button>
              <button
                onClick={handleSaveAndNext}
                className="flex-1 px-4 py-3 bg-green-600 active:bg-green-700 text-white rounded-lg text-sm font-bold transition-colors flex items-center justify-center gap-1"
              >
                {t.saveAndNext}
                <FiChevronRight className="w-5 h-5" />
              </button>
            </div>
            {/* Top Row */}
            <div className="flex gap-2">
              <button
                onClick={handleMarkForReviewAndNext}
                className="flex-1 px-3 py-2 border-2 border-purple-600 text-purple-600 rounded-lg text-sm font-semibold active:bg-purple-50 transition-colors flex items-center justify-center gap-1"
              >
                <FiFlag className="w-4 h-4" />
                <span>{t.markForReview}</span>
              </button>
              <button
                onClick={handleClearResponse}
                disabled={!answers[currentQuestion]}
                className="flex-1 px-3 py-2 border-2 border-gray-300 text-gray-700 rounded-lg text-sm font-semibold active:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t.clearResponse}
              </button>
            </div>
            {/* Bottom Row */}
          
          </div>
        </div>

        {/* Question Palette - Desktop Sidebar Only */}
        <div className="hidden lg:block lg:w-80 bg-gray-50 border-l border-gray-200">
          <div className="h-full flex flex-col">
            {/* Palette Header */}
            <div className="p-4 border-b border-gray-200 bg-white">
              <h3 className="font-bold text-gray-900 text-base mb-4">{t.questionPalette}</h3>

              {/* Status Legend */}
              <div className="space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-green-600"></div>
                    <span className="text-gray-700 text-xs">{t.answered}</span>
                  </div>
                  <span className="font-bold text-gray-900">{answeredCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-red-600"></div>
                    <span className="text-gray-700 text-xs">{t.notAnswered}</span>
                  </div>
                  <span className="font-bold text-gray-900">{notAnsweredCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-gray-300"></div>
                    <span className="text-gray-700 text-xs">{t.notVisited}</span>
                  </div>
                  <span className="font-bold text-gray-900">{notVisitedCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-purple-600"></div>
                    <span className="text-gray-700 text-xs">{t.markedForReview}</span>
                  </div>
                  <span className="font-bold text-gray-900">{reviewedCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-purple-600 relative">
                      <div className="absolute inset-1 bg-green-600 rounded-sm"></div>
                    </div>
                    <span className="text-gray-700 text-xs">{t.answeredMarkedForReview}</span>
                  </div>
                  <span className="font-bold text-gray-900">{answeredAndMarkedCount}</span>
                </div>
              </div>
            </div>

            {/* Question Grid */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-5 gap-2">
                {questions.map((_, index) => {
                  const status = getQuestionStatus(index);
                  const isCurrent = index === currentQuestion;
                  
                  let bgClass = 'bg-gray-300 text-gray-700 hover:bg-gray-400';
                  if (status === 'answered-marked') {
                    bgClass = 'bg-purple-600 text-white relative';
                  } else if (status === 'marked') {
                    bgClass = 'bg-purple-600 text-white';
                  } else if (status === 'answered') {
                    bgClass = 'bg-green-600 text-white';
                  } else if (status === 'not-answered') {
                    bgClass = 'bg-red-600 text-white';
                  }
                  
                  return (
                    <button
                      key={index}
                      onClick={() => {
                      
                        setCurrentQuestion(index);
                        setVisited(prev => ({ ...prev, [index]: true }));
                      }}
                      className={`aspect-square rounded font-bold text-sm transition-all ${bgClass} ${
                        isCurrent ? 'ring-4 ring-blue-400 scale-110' : 'hover:scale-105'
                      }`}
                    >
                      {status === 'answered-marked' && (
                        <div className="absolute inset-1.5 bg-green-600 rounded-sm flex items-center justify-center">
                          {index + 1}
                        </div>
                      )}
                      {status !== 'answered-marked' && index + 1}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Submit Confirmation Modal - Testbook Style */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">{t.submitExam}</h3>
              <p className="text-gray-600 text-sm mt-1">{t.reviewAnswers}</p>
            </div>

            {/* Content */}
            <div className="p-4">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <div className="text-center p-3 bg-green-50 rounded-md border border-green-200">
                  <div className="text-2xl font-bold text-green-700">{answeredCount}</div>
                  <div className="text-xs text-green-600 mt-1">{t.answered}</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-md border border-red-200">
                  <div className="text-2xl font-bold text-red-700">{notAnsweredCount}</div>
                  <div className="text-xs text-red-600 mt-1">{t.notAnswered}</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-md border border-gray-200">
                  <div className="text-2xl font-bold text-gray-700">{notVisitedCount}</div>
                  <div className="text-xs text-gray-600 mt-1">{t.notVisited}</div>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-md border border-purple-200">
                  <div className="text-2xl font-bold text-purple-700">{reviewedCount}</div>
                  <div className="text-xs text-purple-600 mt-1">{t.markedForReview}</div>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-md border border-blue-200">
                  <div className="text-2xl font-bold text-blue-700">{questions.length}</div>
                  <div className="text-xs text-blue-600 mt-1">Total</div>
                </div>
              </div>

              {/* Warning or Success Message */}
              {answeredCount < questions.length ? (
                <div className="bg-orange-50 border-l-4 border-orange-500 p-3 mb-4">
                  <div className="flex items-start">
                    <FiAlertCircle className="w-5 h-5 text-orange-500 mt-0.5 mr-2 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-orange-900 text-sm">{t.warning}</p>
                      <p className="text-orange-700 text-xs mt-0.5">
                        {t.warningMessage.replace('{count}', questions.length - answeredCount)}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 border-l-4 border-green-500 p-3 mb-4">
                  <div className="flex items-center">
                    <FiCheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                    <p className="font-bold text-green-900 text-sm">{t.allAnswered}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="p-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                {t.goBack}
              </button>
              <button
                onClick={handleSubmit}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors"
              >
                {t.submitNow}
              </button>
            </div>
          </div>
        </div>
      )}

   
      </div>
    </>
  );
};

export default QuizPage;

